import {
  CHAT_PROVIDER_CAPABILITIES,
  DISABLED_CHAT_CAPABILITIES,
  chatErrorResult,
  createChatResult,
  disabledChatResult,
  safeChatOperation,
  type ChatAdapter,
  type ChatClient,
  type ChatEventMetadata,
  type ChatProviderName,
  type ChatResult,
  type ChatScriptDescriptor,
  type ChatTheme,
  type ChatUser,
  type EnabledChatConfig,
  type PublicChatConfig,
} from "./provider";

export type ChatLifecycleState = "idle" | "loading" | "ready" | "failed";

type ScriptListener = (script: ChatScriptDescriptor) => void;
type ChatAdapterLoader = (config: EnabledChatConfig) => Promise<ChatAdapter>;

interface PendingChatOperation {
  operation: string;
  execute: (adapter: ChatAdapter) => Promise<ChatResult>;
  resolve: (result: ChatResult) => void;
}

let activeProvider: ChatProviderName | null = null;

async function loadChatAdapter(config: EnabledChatConfig): Promise<ChatAdapter> {
  switch (config.provider) {
    case "crisp": {
      const adapterModule = await import("./providers/crisp");
      return adapterModule.createCrispAdapter(config);
    }
    case "intercom": {
      const adapterModule = await import("./providers/intercom");
      return adapterModule.createIntercomAdapter(config);
    }
    case "pylon": {
      const adapterModule = await import("./providers/pylon");
      return adapterModule.createPylonAdapter(config);
    }
  }
}

export class ChatManager implements ChatClient {
  readonly provider: ChatProviderName | null;
  readonly capabilities;

  private readonly config: PublicChatConfig;
  private readonly loadAdapter: ChatAdapterLoader;
  private readonly scriptListeners = new Set<ScriptListener>();
  private readonly pendingOperations: PendingChatOperation[] = [];
  private adapter: ChatAdapter | null = null;
  private script: ChatScriptDescriptor | null = null;
  private state: ChatLifecycleState = "idle";
  private currentTheme: ChatTheme = "dark";
  private appliedTheme: ChatTheme | null = null;
  private appliedPathname = "";
  private drainPromise: Promise<void> | null = null;
  private preparationPromise: Promise<ChatResult> | null = null;
  private scriptReadyPromise: Promise<ChatResult> | null = null;
  private initializationPromise: Promise<ChatResult>;
  private resolveInitialization: ((result: ChatResult) => void) | null = null;
  private initializationSettled = false;
  private failureResult: ChatResult | null = null;

  constructor(config: PublicChatConfig, adapterLoader: ChatAdapterLoader = loadChatAdapter) {
    this.config = config;
    this.loadAdapter = adapterLoader;
    this.provider = config.enabled ? config.provider : null;
    this.capabilities = config.enabled
      ? CHAT_PROVIDER_CAPABILITIES[config.provider]
      : DISABLED_CHAT_CAPABILITIES;

    if (config.enabled) {
      this.initializationPromise = new Promise<ChatResult>((resolve) => {
        this.resolveInitialization = resolve;
      });
    } else {
      this.initializationPromise = Promise.resolve(disabledChatResult("initialize"));
      this.initializationSettled = true;
    }

  }

  subscribeToScript(listener: ScriptListener) {
    this.scriptListeners.add(listener);
    if (this.script) {
      listener(this.script);
    }

    return () => {
      this.scriptListeners.delete(listener);
    };
  }

  get lifecycleState(): ChatLifecycleState {
    return this.state;
  }

  private publishScript(script: ChatScriptDescriptor) {
    this.script = script;
    this.scriptListeners.forEach((listener) => listener(script));
  }

  private settleInitialization(result: ChatResult) {
    if (this.initializationSettled) {
      return;
    }
    this.initializationSettled = true;
    this.resolveInitialization?.(result);
    this.resolveInitialization = null;
  }

  private recordFailure(result: ChatResult) {
    this.state = "failed";
    this.failureResult = result;
    this.settleInitialization(result);
    this.failPendingOperations();
  }

  private failPendingOperations() {
    while (this.pendingOperations.length > 0) {
      const pending = this.pendingOperations.shift();
      if (pending) {
        pending.resolve(chatErrorResult(this.provider, pending.operation));
      }
    }
  }

  private drainPendingOperations = (): Promise<void> => {
    if (this.state !== "ready") {
      return Promise.resolve();
    }

    if (this.drainPromise) {
      return this.drainPromise;
    }

    this.drainPromise = (async () => {
      while (this.state === "ready" && this.pendingOperations.length > 0) {
        const pending = this.pendingOperations.shift();
        if (!pending) {
          continue;
        }

        const adapter = this.adapter;
        if (!adapter) {
          pending.resolve(chatErrorResult(this.provider, pending.operation));
          continue;
        }

        const result = await safeChatOperation(this.provider, pending.operation, () =>
          pending.execute(adapter),
        );
        pending.resolve(result);
      }
    })().finally(() => {
      this.drainPromise = null;
      if (this.state === "ready" && this.pendingOperations.length > 0) {
        void this.drainPendingOperations();
      }
    });

    return this.drainPromise;
  };

  private applyTheme = async (
    adapter: ChatAdapter,
    theme: ChatTheme,
  ): Promise<ChatResult> => {
    if (this.appliedTheme === theme) {
      return createChatResult(this.provider, "set_theme");
    }

    const result = await adapter.setTheme(theme);
    if (result.status !== "error") {
      this.appliedTheme = theme;
    }
    return result;
  };

  private applyRoute = async (
    adapter: ChatAdapter,
    pathname: string,
  ): Promise<ChatResult> => {
    if (!pathname || this.appliedPathname === pathname) {
      return createChatResult(this.provider, "update_route");
    }

    const result = await adapter.updateRoute(pathname);
    if (result.status !== "error") {
      this.appliedPathname = pathname;
    }
    return result;
  };

  private ensurePrepared = async (): Promise<ChatResult> => {
    if (!this.config.enabled) {
      return disabledChatResult("prepare");
    }

    if (this.failureResult) {
      return this.failureResult;
    }

    if (this.preparationPromise) {
      return this.preparationPromise;
    }

    const config = this.config;
    this.state = "loading";
    this.preparationPromise = safeChatOperation(config.provider, "prepare_adapter", async () => {
      if (activeProvider && activeProvider !== config.provider) {
        throw new Error("A different chat provider is already active.");
      }
      activeProvider = config.provider;

      const adapter = await this.loadAdapter(config);
      const preparedTheme = this.currentTheme;
      const preparation = await safeChatOperation(config.provider, "prepare", () =>
        adapter.prepare(preparedTheme),
      );

      if (preparation.status !== "ok") {
        return preparation;
      }

      this.adapter = adapter;
      this.appliedTheme = preparedTheme;
      this.publishScript(adapter.script);
      return createChatResult(config.provider, "prepare_adapter");
    }).then((result) => {
      if (result.status !== "ok") {
        this.recordFailure(result);
      }
      return result;
    });

    return this.preparationPromise;
  };

  private invoke = (
    operation: string,
    callback: (adapter: ChatAdapter) => Promise<ChatResult>,
  ): Promise<ChatResult> => {
    if (!this.config.enabled) {
      return Promise.resolve(disabledChatResult(operation));
    }

    if (this.failureResult) {
      return Promise.resolve(chatErrorResult(this.provider, operation));
    }

    const promise = new Promise<ChatResult>((resolve) => {
      this.pendingOperations.push({ operation, execute: callback, resolve });
    });

    if (this.state === "ready") {
      void this.drainPendingOperations();
    } else {
      void this.ensurePrepared().catch(() => {
        this.recordFailure(chatErrorResult(this.provider, "prepare_adapter"));
      });
    }

    return promise;
  };

  initialize = async (): Promise<ChatResult> => {
    if (!this.config.enabled) {
      return disabledChatResult("initialize");
    }

    const preparation = await this.ensurePrepared();
    if (preparation.status !== "ok") {
      return preparation;
    }

    return this.initializationPromise;
  };

  handleScriptReady = async (): Promise<ChatResult> => {
    if (!this.config.enabled) {
      return disabledChatResult("script_ready");
    }
    const provider = this.config.provider;

    if (this.scriptReadyPromise) {
      return this.scriptReadyPromise;
    }

    this.scriptReadyPromise = safeChatOperation(
      provider,
      "script_ready",
      async () => {
        const preparation = await this.ensurePrepared();
        if (preparation.status !== "ok") {
          return preparation;
        }
        if (!this.adapter) {
          throw new Error("The selected chat adapter was not prepared.");
        }

        const readiness = await this.adapter.onScriptReady();
        if (readiness.status !== "ok") {
          return readiness;
        }
        if (this.failureResult) {
          return this.failureResult;
        }

        this.state = "ready";
        return createChatResult(provider, "initialize");
      },
    ).then(async (result) => {
      if (result.status === "ok") {
        this.settleInitialization(result);
        await this.drainPendingOperations();
      } else {
        this.recordFailure(result);
      }
      return result;
    });

    return this.scriptReadyPromise;
  };

  handleScriptError = async (): Promise<ChatResult> => {
    if (!this.config.enabled) {
      return disabledChatResult("load_script");
    }

    const result = await safeChatOperation(this.config.provider, "load_script", () => {
      throw new Error("The remote chat script failed to load.");
    });
    this.recordFailure(result);
    return result;
  };

  identifyUser = (user: ChatUser) =>
    this.invoke("identify_user", (adapter) => adapter.identifyUser(user));

  logout = () => this.invoke("logout", (adapter) => adapter.logout());

  show = () => this.invoke("show", (adapter) => adapter.show());

  hide = () => this.invoke("hide", (adapter) => adapter.hide());

  open = () => this.invoke("open", (adapter) => adapter.open());

  close = () => this.invoke("close", (adapter) => adapter.close());

  trackEvent = (name: string, metadata?: ChatEventMetadata) =>
    this.invoke("track_event", (adapter) => adapter.trackEvent(name, metadata));

  setTheme = async (theme: ChatTheme): Promise<ChatResult> => {
    this.currentTheme = theme;
    return this.invoke("set_theme", (adapter) => this.applyTheme(adapter, theme));
  };

  updateRoute = async (pathname: string): Promise<ChatResult> => {
    return this.invoke("update_route", (adapter) => this.applyRoute(adapter, pathname));
  };
}

export const createChatManager = (config: PublicChatConfig) => new ChatManager(config);
